import React, { FC } from 'react'
import classNames from 'classnames'
import { FlexColumn } from '@/atoms/FlexContainers'
import { Image } from '@/atoms/Image'
import { EyebrowMD, HeadingXS, ParagraphLG } from '@/atoms/Text'
import { ThemeContextProvider } from '@/contexts/ThemeContext'
import { MinimalistFooter } from '@/organisms/MinimalistFooter'
import { Offer } from '@/services/PifService'
import { useTranslate } from '@/utils/translate/translate-client'
import { ExpressExclusiveScene } from '@/views/ExpressCheckoutView/ExpressCheckoutComplete/components/ExpressExclusiveScene'
import { ExpressReceipt } from '@/views/ExpressCheckoutView/ExpressCheckoutComplete/components/ExpressReceipt'
import { ExpressShare } from '@/views/ExpressCheckoutView/ExpressCheckoutComplete/components/ExpressShare'
import { RadialGradient } from '@/views/ExpressCheckoutView/components/RadialGradient'
import { CheckoutLegalTerms } from '@/views/PayItForward/PayItForwardProjectCheckoutOfferView/CheckoutLegalTerms'
import { getBonusAccessConfig } from '../utils'
import { ExpressCheckoutCompleteBonusAccess } from './ExpressCheckoutCompleteBonusAccess'

interface NewExpressCheckoutCompleteProps {
  offer: Offer
  projectSlug: string
  projectName: string
  isDarkMode?: boolean
}

export const ExpressCheckoutComplete: FC<NewExpressCheckoutCompleteProps> = ({
  offer,
  projectSlug,
  projectName,
  isDarkMode = false,
}) => {
  const { t } = useTranslate('pif')

  const bonusAccessConfig = getBonusAccessConfig(projectSlug)

  if (bonusAccessConfig) {
    return <ExpressCheckoutCompleteBonusAccess offer={offer} projectSlug={projectSlug} config={bonusAccessConfig} />
  }

  return (
    <ThemeContextProvider isDarkMode={isDarkMode}>
      <div className={classNames(isDarkMode ? 'bg-core-gray-950' : 'bg-core-gray-100')}>
        <FlexColumn className="relative mx-auto max-w-[640px] gap-10 px-6 pb-16 pt-8">
          {isDarkMode && <RadialGradient />}
          <Image
            className="mx-auto"
            src="/v1720204976/pif-ticket-illustration-01_2x.webp"
            alt=""
            width={202}
            height={122}
          />
          <FlexColumn className="gap-2 text-center">
            <EyebrowMD color={isDarkMode ? 'core-gray-400' : 'core-gray-700'} className="uppercase">
              {t('orderCompleted', 'Order Completed')}
            </EyebrowMD>
            <HeadingXS weight="bold" color={isDarkMode ? 'white' : 'core-gray-950'}>
              {t('yourGenerosityShines', 'Your Generosity Shines')}
            </HeadingXS>
            <ParagraphLG className="mt-2" weight="medium" color={isDarkMode ? 'white' : 'core-gray-950'}>
              {t(
                'thankYouForYourGenerousContributionDescription',
                'Thank you for your generous contribution! Paying it Forward lets others experience {{title}}.',
                { title: projectName },
              )}
            </ParagraphLG>
          </FlexColumn>
          <ExpressReceipt projectSlug={projectSlug} offer={offer} />

          <ExpressExclusiveScene projectName={projectName} projectSlug={projectSlug} />
          <ExpressShare projectSlug={projectSlug} projectName={projectName} />
          <CheckoutLegalTerms
            className={classNames('text-center', isDarkMode ? '!text-core-gray-400' : '!text-core-gray-700')}
          />
        </FlexColumn>
      </div>
      <MinimalistFooter className="!pb-12" />
    </ThemeContextProvider>
  )
}
